// -----------------------------------------------------------------------------
// RescheduleDialog — shared reschedule/due-date dialog (Change 3)
//
// Extracted from UpNextCardV2 and PostSendExpandedCard to eliminate divergence.
//
// Features (per Change-3 spec):
//   - Dialog title: "Reschedule this task"
//   - Prominent preview line: "Currently: X → New: Y" (humanized dates)
//   - "Quick pick" section label above preset chips (single-click saves)
//   - "Or choose a date" section label above StaticDateTimePicker
//   - Primary button labelled "Reschedule" (CRM_PRIMARY_BUTTON_SX green)
//   - "Cancel" secondary button
//
// Props:
//   open          — controlled open state
//   onClose       — called on Cancel or backdrop click
//   onConfirm(date: Date) — called with chosen Date; parent handles PATCH logic
//   item          — checklist item (supplies .title, .due_at for preview)
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import moment from 'moment';
import {
    Box, Typography, Button, Stack, TextField,
    Dialog, DialogContent, DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { StaticDateTimePicker } from '@mui/x-date-pickers/StaticDateTimePicker';
import Iconify from 'src/components/Iconify';
import { momentFromApi, formatTaskDate } from 'src/utils/taskDateUtils';
import { CRM_PRIMARY_BUTTON_SX } from 'src/styles/crmButtons';

// All presets are defined as `moment().add(...)` from "now" so they're inherently
// future-relative. Listed in chronological order. The render path also filters
// at runtime via `p.getDate().isAfter(moment())` as a defensive check — any
// future addition that resolves to "now" or earlier (e.g. an edge case where
// the dialog sits open past midnight and 'Today' end-of-day has lapsed) is
// dropped automatically. 2026-06-07: task #82.
const PRESETS = [
    { label: 'Today',      getDate: () => moment().endOf('day') },
    { label: 'Tomorrow',   getDate: () => moment().add(1, 'day').endOf('day') },
    { label: 'In 3 days',  getDate: () => moment().add(3, 'days').endOf('day') },
    { label: 'Next week',  getDate: () => moment().add(1, 'week').endOf('day') },
    { label: 'In 2 weeks', getDate: () => moment().add(2, 'weeks').endOf('day') },
];

export default function RescheduleDialog({ open, onClose, onConfirm, item }) {
    const [calValue, setCalValue] = useState(null);
    const [saving, setSaving] = useState(false);
    // Rename: the user can edit the task title while rescheduling. Seeded from the
    // current title on open; passed back via onConfirm(date, title).
    const [title, setTitle] = useState('');

    // Reset picker when dialog opens — pre-fill with current due date IF it's in
    // the future. Past due dates (overdue tasks) seed the picker with `null` so
    // the calendar doesn't initialize on a disabled cell. 2026-06-07: task #82.
    useEffect(() => {
        if (open) {
            // PICKER value — API date → moment for the AdapterMoment picker.
            const due = item?.due_at ? momentFromApi(item.due_at) : null;
            const usable = due && due.isAfter(moment()) ? due : null;
            setCalValue(usable);
            setTitle(item?.title || '');
            setSaving(false);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Preset chips save immediately (single click = done) — no extra confirm needed.
    const handlePresetSelect = async (date) => {
        if (saving) return;
        setSaving(true);
        try {
            const result = onConfirm ? await onConfirm(date.toDate(), title) : true;
            if (result !== false) onClose();
        } finally {
            setSaving(false);
        }
    };

    const titleChanged = title.trim() !== (item?.title || '').trim() && title.trim().length > 0;
    const selectedDateIsValid = Boolean(calValue && calValue.isAfter(moment()));
    const canConfirm = titleChanged || selectedDateIsValid;
    let confirmLabel = 'Save';
    if (saving) {
        confirmLabel = 'Saving...';
    } else if (selectedDateIsValid) {
        confirmLabel = 'Reschedule';
    }

    // Calendar pick requires explicit "Reschedule" button. Title-only renames are
    // also valid saves, even when the date is unchanged / past / absent.
    // 2026-06-07 (task #82): defensive guard — if the user somehow ends up with a
    // past calValue (e.g. via keyboard input bypassing minDateTime in some MUI
    // versions), do not send it as a due_at change.
    const handleConfirm = async () => {
        if (saving || !canConfirm) return;
        setSaving(true);
        try {
            const result = onConfirm ? await onConfirm(selectedDateIsValid ? calValue.toDate() : null, title) : true;
            if (result !== false) onClose();
        } finally {
            setSaving(false);
        }
    };

    const handleClose = (...args) => {
        if (!saving) onClose(...args);
    };

    const currentLabel = item?.due_at
        ? (formatTaskDate(item.due_at, 'ddd D MMM, HH:mm') || 'Not scheduled')  // DISPLAY
        : 'Not scheduled';

    const newLabel = calValue ? calValue.format('ddd D MMM, HH:mm') : null;

    return (
        <Dialog
            data-testid="reschedule-dialog"
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
            {/* ── Header ─────────────────────────────────────────────── */}
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, bgcolor: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography sx={{
                    fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                    letterSpacing: '0.06em', color: '#6e6e73', mb: 0.5,
                }}>
                    Reschedule this task
                </Typography>
                <Typography sx={{
                    fontSize: '18px', fontWeight: 700, color: '#1d1d1f',
                    letterSpacing: '-0.02em', lineHeight: 1.25,
                }}>
                    {item?.title || 'Reschedule task'}
                </Typography>

                {/* Preview: Currently X → New: Y */}
                <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ mt: 1, gap: 0.75 }}
                >
                    <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                        Currently:{' '}
                        <Box component="span" sx={{ fontWeight: 600, color: '#3c3c43' }}>
                            {currentLabel}
                        </Box>
                    </Typography>
                    {newLabel && (
                        <>
                            <Iconify
                                icon="eva:arrow-forward-fill"
                                width={14}
                                height={14}
                                sx={{ color: '#6e6e73', flexShrink: 0 }}
                            />
                            <Typography sx={{ fontSize: '12px', color: '#078DEE', fontWeight: 600, letterSpacing: '-0.005em' }}>
                                New: {newLabel}
                            </Typography>
                        </>
                    )}
                </Stack>
            </Box>

            {/* ── Body ───────────────────────────────────────────────── */}
            <DialogContent sx={{ p: 0 }}>
                {/* Rename — edit the task title while rescheduling. */}
                <Box sx={{ px: 3, pt: 2 }}>
                    <Typography sx={{
                        pb: 0.75,
                        fontSize: '11px', fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6e6e73',
                    }}>
                        Task name
                    </Typography>
                    <TextField
                        data-testid="reschedule-dialog-title"
                        fullWidth
                        size="small"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        disabled={saving}
                        placeholder="Task name"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' } }}
                    />
                </Box>

                {/* Quick pick section */}
                <Typography sx={{
                    px: 3, pt: 2, pb: 0.75,
                    fontSize: '11px', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6e6e73',
                }}>
                    Quick pick
                </Typography>
                <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ px: 3, pb: 1.5, flexWrap: 'wrap', rowGap: 0.75 }}
                >
                    {/* Drop any preset that resolves to "now" or earlier. All current
                        PRESETS are future-relative, so this is a no-op today; the filter
                        future-proofs against additions and dialog-left-open-past-midnight
                        edge cases. 2026-06-07: task #82. */}
                    {PRESETS
                        .filter((preset) => preset.getDate().isAfter(moment()))
                        .map((preset) => (
                        <Button
                            key={preset.label}
                            size="small"
                            disabled={saving}
                            onClick={() => handlePresetSelect(preset.getDate())}
                            sx={{
                                textTransform: 'none',
                                fontSize: '12px',
                                fontWeight: 500,
                                borderRadius: '8px',
                                py: 0.4,
                                px: 1.25,
                                minWidth: 0,
                                color: '#3c3c43',
                                bgcolor: 'rgba(0,0,0,0.04)',
                                '&:hover': { bgcolor: 'rgba(7,141,238,0.10)', color: '#078DEE' },
                            }}
                        >
                            {preset.label}
                        </Button>
                    ))}
                </Stack>

                {/* Or choose a date section */}
                <Typography sx={{
                    px: 3, pb: 0.75,
                    fontSize: '11px', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6e6e73',
                }}>
                    Or choose a date
                </Typography>
                <Box sx={{ px: 1, pb: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <StaticDateTimePicker
                            displayStaticWrapperAs="desktop"
                            ampm={false}
                            value={calValue}
                            onChange={(newVal) => setCalValue(newVal)}
                            disabled={saving}
                            // 2026-06-07 (task #82): only allow future dates/times.
                            // Was: no constraint → 3-months-back was selectable. minDateTime
                            // disables every cell at or before "now" (re-evaluated each render
                            // so the constraint updates as time advances).
                            minDateTime={moment()}
                            disablePast
                            slotProps={{
                                actionBar: { actions: [] },
                                toolbar: { hidden: true },
                            }}
                            sx={{
                                bgcolor: 'transparent',
                                '& .MuiPickersDay-root.Mui-selected': {
                                    bgcolor: '#078DEE',
                                    '&:hover': { bgcolor: '#0a7bd1' },
                                },
                                '& .MuiPickersDay-today': { borderColor: '#078DEE' },
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            </DialogContent>

            {/* ── Actions ────────────────────────────────────────────── */}
            <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fafafa' }}>
                <Button
                    data-testid="reschedule-dialog-cancel"
                    disabled={saving}
                    onClick={handleClose}
                    sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px', fontWeight: 500 }}
                >
                    Cancel
                </Button>
                <LoadingButton
                    data-testid="reschedule-dialog-confirm"
                    variant="contained"
                    disableElevation
                    loading={saving}
                    // Enable for either a real title change or a valid future date.
                    disabled={saving || !canConfirm}
                    onClick={handleConfirm}
                    sx={{
                        ...CRM_PRIMARY_BUTTON_SX,
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 600,
                        py: 0.75,
                        px: 2.5,
                        borderRadius: '8px',
                    }}
                >
                    {confirmLabel}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

RescheduleDialog.propTypes = {
    /** Controlled open state. */
    open: PropTypes.bool.isRequired,
    /** Called on Cancel or backdrop click. */
    onClose: PropTypes.func.isRequired,
    /** Called with the chosen Date; caller handles PATCH + optimistic update. */
    onConfirm: PropTypes.func,
    /** Checklist item — provides .title and .due_at for the preview line. */
    item: PropTypes.object,
};

RescheduleDialog.defaultProps = {
    onConfirm: null,
    item: null,
};
